// Liquidaciones > Choferes — mismo patrón que LiquidacionesClientes.tsx,
// entidad Driver en vez de Store. Pipeline: legacy.envios.cadete ->
// driver_data.lightdata_username -> Driver -> tarifa DRIVER -> CP/tier
// (tariff_postal_codes) -> precio base -> ×2/Turbo/manual. Sin descuento
// (no es un concepto de Driver). Semana WYN (viernes a jueves), fecha_colecta.
//
// Un Driver sin tarifa configurada IGUAL aparece acá (con "Sin tarifa" y
// sus envíos como pendientes) — mismo criterio que Clientes. Premio Elite y
// Colecta viajan preparados en la respuesta (premioElite/colecta) pero
// todavía sin calcular — el próximo sprint implementa esa lógica.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { downloadAuthenticatedFile } from "../lib/downloadFile";
import DriverDetailModal from "../components/liquidaciones/DriverDetailModal";
import PeriodSelector from "../components/liquidaciones/PeriodSelector";

type Period = {
  key: string;
  from: string;
  to: string;
  label: string;
  fromDisplay: string;
  toDisplay: string;
  // readOnly (ver auditoría Fase 1.5 — "Liquidaciones UX"): true en una
  // semana ya CERRADA, consultada vía ?period=. El backend sigue siendo la
  // autoridad real (PERIOD_CLOSED/DAY_CLOSED) — esto es solo para no
  // mostrar acciones de edición que el server va a rechazar igual.
  readOnly?: boolean;
};

type Summary = {
  totalDeliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  totalAmount: number;
};

type ConfigStatus = "CONFIGURADO" | "CON_PENDIENTES" | "SIN_TARIFA";

type DriverRow = {
  driverId: number | string;
  driverName: string | null;
  lightdataUsername: string | null;
  tariff: { id: number; name: string } | null;
  deliveries: number;
  pricedDeliveries: number;
  unresolvedDeliveries: number;
  totalReparto: number;
  premioElite: number | null;
  colecta: number | null;
  totalFinal: number;
  configStatus: ConfigStatus;
  // Ranking operativo (ver "LYNX — Bloque final: ranking Choferes") —
  // agregaciones puras sobre los mismos datos ya calculados por
  // getRangeRows/Driver Daily Performance/Elite Engine, nunca un motor
  // nuevo. Ver computeChoferesRankingMetrics en driverLiquidaciones.service.js
  // para la definición exacta de numerador/denominador.
  incidentCount: number;
  // incidentCountFlex/incidentCountOther (ver auditoría "LYNX — Auditoría
  // integral pre-liquidación", sección 2/5/6): la columna única mezclaba
  // universos (Flex + Directo/TN). Se separan explícitamente — Directo/TN
  // nunca afectan Premio Elite, pero siguen siendo operativamente
  // interesantes. incidentCount se mantiene por compatibilidad (suma de
  // ambas), ya no se muestra como columna única.
  incidentCountFlex: number;
  incidentCountOther: number;
  eliteEligibleCount: number;
  eliteDeliveredPre21Count: number;
  elitePercentage: number | null; // null = sin universo elite-elegible esta semana (mostrar "—", nunca 0%)
  eliteDaysWon: number;
  eliteDaysLost: number;
};

type ChoferesResponse = {
  ok: boolean;
  period: Period;
  summary: Summary;
  drivers: DriverRow[];
  unmapped: { deliveries: number; distinctCadetes: string[] };
};

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const headerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const periodBadgeStyle: React.CSSProperties = { display: "inline-block", padding: "4px 12px", borderRadius: "999px", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600 };
const emptyStyle: React.CSSProperties = { padding: "32px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const cardsRowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "14px" };
const statLabelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 };
const statValueStyle: React.CSSProperties = { fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", marginTop: "6px" };
const sectionTitleStyle: React.CSSProperties = { margin: "0 0 10px 0", fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" };
const toolbarStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 16px 0 16px", flexWrap: "wrap" };
const toolbarControlsStyle: React.CSSProperties = { display: "flex", gap: "10px", flexWrap: "wrap" };
const searchFieldStyle: React.CSSProperties = { minWidth: "240px" };
const selectStyle: React.CSSProperties = {
  minHeight: "48px",
  padding: "0 14px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt, transparent)",
  color: "var(--text-primary)",
  fontSize: "14px",
};
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const chipStyle: React.CSSProperties = { display: "inline-block", padding: "2px 8px", borderRadius: "999px", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "11px", marginRight: "4px", marginTop: "2px" };

const CONFIG_BADGE: Record<ConfigStatus, { label: string; color: string }> = {
  CONFIGURADO: { label: "Configurado", color: "var(--success, #2ea043)" },
  CON_PENDIENTES: { label: "Con pendientes", color: "#c99a1f" },
  SIN_TARIFA: { label: "Sin tarifa", color: "var(--danger, #d1242f)" },
};

function money(n: number): string {
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Criterio visual (ver pedido "ranking Choferes"): solo donde no hay
// ambigüedad — 0 incidencias es inequívocamente favorable, >0 es una
// advertencia real. % Entrega Elite queda visualmente NEUTRO a propósito:
// no existe hoy un umbral de negocio aprobado (90%=verde, 70%=rojo) y no
// corresponde que una decisión estética invente uno silenciosamente.
function IncidentBadge({ count }: { count: number }) {
  const color = count === 0 ? "var(--success, #2ea043)" : "var(--danger, #d1242f)";
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${color}`, color }}>
      {count}
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card padding="18px">
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </Card>
  );
}

function ConfigBadge({ status }: { status: ConfigStatus }) {
  const b = CONFIG_BADGE[status];
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${b.color}`, color: b.color }}>
      {b.label}
    </span>
  );
}

type SortKey =
  | "name_asc"
  | "name_desc"
  | "deliveries_desc"
  | "deliveries_asc"
  | "total_desc"
  | "total_asc"
  | "tariff"
  | "elite_pct_desc"
  | "elite_pct_asc"
  | "incidents_flex_desc"
  | "incidents_flex_asc"
  | "incidents_other_desc"
  | "incidents_other_asc"
  | "elite_days_won_desc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "name_asc", label: "Nombre A-Z" },
  { value: "name_desc", label: "Nombre Z-A" },
  { value: "deliveries_desc", label: "Envíos ↓" },
  { value: "deliveries_asc", label: "Envíos ↑" },
  { value: "total_desc", label: "Total ↓" },
  { value: "total_asc", label: "Total ↑" },
  { value: "tariff", label: "Tarifa" },
  { value: "elite_pct_desc", label: "% Entrega Elite ↓" },
  { value: "elite_pct_asc", label: "% Entrega Elite ↑" },
  { value: "incidents_flex_desc", label: "Incidencias Flex ↓" },
  { value: "incidents_flex_asc", label: "Incidencias Flex ↑" },
  { value: "incidents_other_desc", label: "Otras incidencias ↓" },
  { value: "incidents_other_asc", label: "Otras incidencias ↑" },
  { value: "elite_days_won_desc", label: "Días Elite ganados ↓" },
];

function driverLabel(d: { driverName: string | null; lightdataUsername: string | null }): string {
  return d.driverName || d.lightdataUsername || "—";
}

// elitePercentage puede ser null (sin universo elite-elegible esa semana,
// ver DriverRow): se ordena siempre al final, sin importar la dirección —
// "sin dato" no es ni el mejor ni el peor valor.
function comparePct(a: DriverRow, b: DriverRow, dir: 1 | -1): number {
  if (a.elitePercentage === null && b.elitePercentage === null) return 0;
  if (a.elitePercentage === null) return 1;
  if (b.elitePercentage === null) return -1;
  return dir * (b.elitePercentage - a.elitePercentage);
}

function sortDrivers(drivers: DriverRow[], sortKey: SortKey): DriverRow[] {
  const sorted = [...drivers];
  switch (sortKey) {
    case "name_desc":
      return sorted.sort((a, b) => driverLabel(b).localeCompare(driverLabel(a)));
    case "deliveries_desc":
      return sorted.sort((a, b) => b.deliveries - a.deliveries);
    case "deliveries_asc":
      return sorted.sort((a, b) => a.deliveries - b.deliveries);
    case "total_desc":
      return sorted.sort((a, b) => b.totalFinal - a.totalFinal);
    case "total_asc":
      return sorted.sort((a, b) => a.totalFinal - b.totalFinal);
    case "tariff":
      return sorted.sort((a, b) => (a.tariff?.name || "").localeCompare(b.tariff?.name || "") || driverLabel(a).localeCompare(driverLabel(b)));
    case "elite_pct_desc":
      return sorted.sort((a, b) => comparePct(a, b, 1));
    case "elite_pct_asc":
      return sorted.sort((a, b) => comparePct(a, b, -1));
    case "incidents_flex_desc":
      return sorted.sort((a, b) => b.incidentCountFlex - a.incidentCountFlex);
    case "incidents_flex_asc":
      return sorted.sort((a, b) => a.incidentCountFlex - b.incidentCountFlex);
    case "incidents_other_desc":
      return sorted.sort((a, b) => b.incidentCountOther - a.incidentCountOther);
    case "incidents_other_asc":
      return sorted.sort((a, b) => a.incidentCountOther - b.incidentCountOther);
    case "elite_days_won_desc":
      return sorted.sort((a, b) => b.eliteDaysWon - a.eliteDaysWon);
    case "name_asc":
    default:
      return sorted.sort((a, b) => driverLabel(a).localeCompare(driverLabel(b)));
  }
}

export default function LiquidacionesChoferes() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // ?period= (ver auditoría Fase 1.5 — "Liquidaciones UX"): mismo contrato
  // que ya usan Dashboard/Historial/los modales de detalle — ausente =
  // semana WYN actual, con valor = esa semana histórica (cerrada). Vivir en
  // la URL (no en state local) es deliberado: un refresh del navegador o un
  // link compartido conservan el período, sin estado global nuevo.
  const periodKey = searchParams.get("period");
  const periodSuffix = periodKey ? `?period=${encodeURIComponent(periodKey)}` : "";
  const [data, setData] = useState<ChoferesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ id: number | string; name: string } | null>(null);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name_asc");
  const [downloadingAll, setDownloadingAll] = useState(false);

  const loadChoferes = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);

    return authFetch(`/v1/liquidaciones/choferes${periodSuffix}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar Liquidaciones > Choferes");
        setData(payload);
      })
      .catch((e: any) => {
        setError(e.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, periodSuffix]);

  // periodSuffix en las deps: cambiar de período dispara el mismo fetch que
  // el mount inicial — nunca una segunda implementación de carga. El
  // refresh manual (botón "Actualizar") llama exactamente esta misma
  // función, sobre el período ACTUALMENTE seleccionado — nunca vuelve
  // silenciosamente a la semana actual.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve(loadChoferes()).then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [loadChoferes]);

  const filteredDrivers = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    const base = q
      ? data.drivers.filter((d) => driverLabel(d).toLowerCase().includes(q) || (d.lightdataUsername || "").toLowerCase().includes(q))
      : data.drivers;
    return sortDrivers(base, sortKey);
  }, [data, search, sortKey]);

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      // El ZIP exporta exactamente el período seleccionado (ver auditoría
      // Fase 1.5): si se está viendo una semana histórica, descarga esa
      // semana — nunca la actual por default.
      await downloadAuthenticatedFile(`/v1/liquidaciones/choferes/export${periodSuffix}`, token, "liquidaciones_choferes.zip");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el ZIP masivo.");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Liquidaciones</h1>
          <p style={subtitleStyle}>Choferes — liquidación semanal por Driver.</p>
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <PeriodSelector
            periodKey={periodKey}
            onChange={(next) => {
              setSearchParams(next ? { period: next } : {});
            }}
          />
          <Button variant="secondary" onClick={() => loadChoferes()} disabled={loading}>
            {loading ? "Actualizando…" : "Actualizar"}
          </Button>
          {data && (
            <Button variant="secondary" onClick={handleDownloadAll} disabled={downloadingAll}>
              {downloadingAll ? "Generando…" : "Descargar todas"}
            </Button>
          )}
        </div>
      </div>

      {loading && <Card>Cargando…</Card>}

      {!loading && error && (
        <Card>
          <span style={{ color: "var(--danger, #d1242f)" }}>{error}</span>
        </Card>
      )}

      {!loading && !error && data && (
        <>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <span style={periodBadgeStyle}>
              Semana WYN: {data.period.fromDisplay} → {data.period.toDisplay}
            </span>
            {data.period.readOnly && (
              <span style={{ ...periodBadgeStyle, borderColor: "#c99a1f", color: "#c99a1f" }}>
                Semana cerrada · Solo lectura
              </span>
            )}
          </div>

          <div style={cardsRowStyle}>
            <StatCard label="Envíos" value={String(data.summary.totalDeliveries)} />
            <StatCard label="Valorizados" value={String(data.summary.pricedDeliveries)} />
            <StatCard label="Pendientes" value={String(data.summary.unresolvedDeliveries)} />
            <StatCard label="Total a pagar" value={money(data.summary.totalAmount)} />
          </div>

          <Card padding="0">
            <div style={toolbarStyle}>
              <h2 style={sectionTitleStyle}>Choferes ({filteredDrivers.length})</h2>
              <div style={toolbarControlsStyle}>
                <div style={searchFieldStyle}>
                  <Input placeholder="Buscar chofer…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select style={selectStyle} value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ padding: "12px 16px 0 16px" }} />
            {filteredDrivers.length === 0 ? (
              <div style={emptyStyle}>{data.drivers.length === 0 ? "Sin choferes vinculados con envíos esta semana." : "Ningún chofer coincide con la búsqueda."}</div>
            ) : (
              <div style={{ ...tableWrapStyle, border: "none", borderRadius: 0 }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Chofer</th>
                      <th style={thStyle}>Tarifa</th>
                      <th style={thStyle}>Estado</th>
                      <th style={thStyle}>Envíos</th>
                      <th style={thStyle}>Valorizados</th>
                      <th style={thStyle}>Pendientes</th>
                      <th style={thStyle} title="Reprogramado, Nadie en el domicilio, Demorado o En camino sin resolución al cierre — SOLO origen MercadoLibre/Flex. No incluye entregas <21hs que perdieron el Elite por otro envío.">
                        Incidencias Flex
                      </th>
                      <th style={thStyle} title="Mismas categorías que Incidencias Flex, pero origen Directo/Tienda Nube — nunca afectan el Premio Elite, pero siguen siendo operativamente relevantes.">
                        Otras incidencias
                      </th>
                      <th style={thStyle} title="Entregas individuales antes de las 21hs / total elegible para Elite (excluye Turbo y Directo/TN). Mide performance operativa individual, no premios cobrados.">
                        % Entrega Elite
                      </th>
                      <th style={thStyle}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrivers.map((d) => (
                      <tr
                        key={String(d.driverId)}
                        style={trClickableStyle}
                        onClick={() => setSelected({ id: d.driverId, name: driverLabel(d) })}
                      >
                        <td style={tdStyle}>
                          {driverLabel(d)}
                          {d.lightdataUsername && (
                            <div>
                              <span style={chipStyle}>{d.lightdataUsername}</span>
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>{d.tariff ? d.tariff.name : "Sin tarifa"}</td>
                        <td style={tdStyle}><ConfigBadge status={d.configStatus} /></td>
                        <td style={tdStyle}>{d.deliveries}</td>
                        <td style={tdStyle}>{d.pricedDeliveries}</td>
                        <td style={tdStyle}>{d.unresolvedDeliveries}</td>
                        <td style={tdStyle}><IncidentBadge count={d.incidentCountFlex} /></td>
                        <td style={tdStyle}><IncidentBadge count={d.incidentCountOther} /></td>
                        <td style={tdStyle}>{d.elitePercentage === null ? "—" : `${d.elitePercentage.toLocaleString("es-AR", { maximumFractionDigits: 1 })}%`}</td>
                        <td style={tdStyle}>{d.tariff ? money(d.totalFinal) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card padding="0">
            <div style={{ padding: "16px" }}>
              <h2 style={sectionTitleStyle}>Sin vincular</h2>
              <p style={{ ...subtitleStyle, marginBottom: "10px" }}>
                Envíos con un cadete de LightData que todavía no está vinculado a ningún Driver Lynx — {data.unmapped.deliveries} envío(s) en {data.unmapped.distinctCadetes.length} username(s).
              </p>
              {data.unmapped.distinctCadetes.length === 0 ? (
                <div style={emptyStyle}>{periodKey ? "No hay envíos sin vincular en esta semana." : "No hay envíos sin vincular en la semana actual."}</div>
              ) : (
                <div>
                  {data.unmapped.distinctCadetes.map((n) => (
                    <span key={n} style={chipStyle}>{n}</span>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </>
      )}

      <DriverDetailModal
        open={selected !== null}
        driverId={selected?.id ?? null}
        driverName={selected?.name ?? null}
        token={token}
        onClose={() => setSelected(null)}
        periodKey={periodKey}
        onOverrideChanged={loadChoferes}
      />
    </div>
  );
}
